/**
 * Same-origin bus so the lobby tab (challenge peerId in the join URL) can sync
 * verified game state into the host board tab, which uses a different PeerJS id.
 */

import { lobbyDebug } from './lobbyDebug.js';

export const GAME_LOCAL_BUS_CHANNEL = 'dechess-game-bus-v1';
const STORAGE_KEY = 'dechess-game-bus-v1:pulse';

function canUseBroadcastChannel() {
  return typeof BroadcastChannel !== 'undefined';
}

function canUseLocalStorage() {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
}

function publishBusMessage(message) {
  let published = false;
  if (canUseBroadcastChannel()) {
    try {
      const channel = new BroadcastChannel(GAME_LOCAL_BUS_CHANNEL);
      channel.postMessage(message);
      channel.close();
      published = true;
    } catch {
      // Fall through.
    }
  }
  if (canUseLocalStorage()) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
      localStorage.removeItem(STORAGE_KEY);
      published = true;
    } catch {
      // Ignore quota / private mode.
    }
  }
  return published;
}

function subscribeBus(deliver) {
  let channel;
  const onChannelMessage = (event) => deliver(event?.data);
  const onStorage = (event) => {
    if (event.key !== STORAGE_KEY || !event.newValue) {
      return;
    }
    try {
      deliver(JSON.parse(event.newValue));
    } catch {
      // Ignore malformed pulses.
    }
  };

  if (canUseBroadcastChannel()) {
    try {
      channel = new BroadcastChannel(GAME_LOCAL_BUS_CHANNEL);
      channel.addEventListener('message', onChannelMessage);
    } catch {
      channel = null;
    }
  }
  if (canUseLocalStorage() && typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }

  return () => {
    if (channel) {
      try {
        channel.removeEventListener('message', onChannelMessage);
        channel.close();
      } catch {
        // Ignore.
      }
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
}

export function publishLocalGameUpdate(game, { source = 'lobby-proxy' } = {}) {
  if (!game?.id) {
    return { published: false };
  }
  const message = {
    type: 'dechess-local-game-update',
    gameId: game.id,
    game,
    source,
    sentAt: Date.now()
  };
  const published = publishBusMessage(message);
  lobbyDebug('game-local-bus-publish', {
    gameId: game.id,
    revision: game.revision,
    status: game.status,
    published,
    source
  });
  return { published };
}

export function subscribeLocalGameUpdate(onMessage) {
  if (typeof onMessage !== 'function') {
    return () => undefined;
  }
  return subscribeBus((raw) => {
    if (!raw || raw.type !== 'dechess-local-game-update' || !raw.game?.id) {
      return;
    }
    lobbyDebug('game-local-bus-receive', {
      gameId: raw.gameId,
      revision: raw.game?.revision,
      source: raw.source
    });
    onMessage(raw);
  });
}

/** Relay signed game protocol envelopes between lobby and board tabs. */
export function publishLocalGameWire({
  gameId,
  messageType,
  payload,
  senderId = '',
  source = 'board'
} = {}) {
  if (!gameId || !messageType) {
    return { published: false };
  }
  const message = {
    type: 'dechess-local-game-wire',
    gameId,
    messageType,
    payload: payload || {},
    senderId,
    source,
    sentAt: Date.now()
  };
  const published = publishBusMessage(message);
  lobbyDebug('game-local-wire-publish', {
    gameId,
    messageType,
    published,
    source
  });
  return { published };
}

export function subscribeLocalGameWire(onMessage) {
  if (typeof onMessage !== 'function') {
    return () => undefined;
  }
  return subscribeBus((raw) => {
    if (!raw || raw.type !== 'dechess-local-game-wire' || !raw.gameId || !raw.messageType) {
      return;
    }
    lobbyDebug('game-local-wire-receive', {
      gameId: raw.gameId,
      messageType: raw.messageType,
      source: raw.source
    });
    onMessage(raw);
  });
}
